import type { FeatureRow } from './Features';

/**
 * One product bucket from GET /features/issueId/… (slideout Features).
 * Some responses use `feature`, others `features`, for the raw feature array.
 */
export interface FeaturesReorderGroup {
  productName?: string;
  feature?: unknown[];
  features?: unknown[];
}

interface RawFeatureRecord {
  featureId?: string;
  id?: string;
  name?: string;
  displayOrder?: number;
}

/** Locate a product group by `groupKey` (same key used for session storage / Features). */
export function findGroupByKey(
  groups: FeaturesReorderGroup[] | null | undefined,
  groupKey: string
): FeaturesReorderGroup | null {
  if (!groups?.length) return null;
  return (
    groups.find((g, idx) => getFeaturesGroupKey(g, idx) === groupKey) ?? null
  );
}

/** Matches FeaturesContainer group key: productName or `group-${index}` */
export function getFeaturesGroupKey(
  group: { productName?: string } | null | undefined,
  index: number
): string {
  return `${group?.productName || `group-${index}`}`;
}

/**
 * Reorder raw API feature objects to match `orderedRows` (by feature id).
 * Appends any raw rows missing from orderedRows at the end (stable).
 */
export function reorderRawFeaturesByRows(
  rawList: RawFeatureRecord[],
  orderedRows: FeatureRow[]
): RawFeatureRecord[] {
  const keyOf = (f: RawFeatureRecord) => String(f.featureId ?? f.id ?? '');
  const rawById = new Map<string, RawFeatureRecord>();
  for (const f of rawList) {
    const k = keyOf(f);
    if (k) rawById.set(k, f);
  }
  const ordered: RawFeatureRecord[] = [];
  const seen = new Set<string>();
  for (const row of orderedRows) {
    const id = String(row.id);
    const item = rawById.get(id);
    if (item) {
      ordered.push(item);
      seen.add(id);
    }
  }
  for (const f of rawList) {
    const k = keyOf(f);
    if (k && !seen.has(k)) ordered.push(f);
  }
  return ordered;
}

/** Apply new order for one product group; leaves other groups unchanged. */
export function mergeReorderIntoGroups(
  groups: FeaturesReorderGroup[],
  groupKey: string,
  orderedRows: FeatureRow[]
): FeaturesReorderGroup[] {
  return groups.map((g, idx) => {
    if (getFeaturesGroupKey(g, idx) !== groupKey) return g;
    const hasFeature = Array.isArray(g.feature);
    const hasFeatures = Array.isArray(g.features);
    const raw = (hasFeature ? g.feature : hasFeatures ? g.features : []) as
      | RawFeatureRecord[]
      | undefined;
    const reordered = reorderRawFeaturesByRows(raw ?? [], orderedRows);
    return {
      ...g,
      ...(hasFeature ? { feature: reordered } : {}),
      ...(hasFeatures ? { features: reordered } : {}),
      ...(!hasFeature && !hasFeatures ? { feature: reordered } : {})
    };
  });
}
